import { useState } from "react";

import Body from "client/common/components/layouts/Body";
import Container from "client/common/components/layouts/Container";
import PillButton from "client/common/components/buttons/PillButton";
import HorizontalOverflow from "client/common/components/display/HorizontalOverflow";
import LoadingContainer from "client/common/components/loading/LoadingContainer";
import FetchErrorContainer from "client/common/components/errors/FetchErrorContainer";
import { useClinic } from "client/clinics/hooks";
import { urls } from "client/routes";
import { useViewItemListOnVisible } from "client/analytics/utils";
import CatalogFilters from "client/home/components/CatalogFilters";

import ProductLineCard from "./ProductLineCard";
import BrowseSupport from "./BrowseSupport";
import { useBrowseQuery } from "../hooks";
import PreFooter from "./PreFooter";
import CategoryCard from "./CategoryCard";
import {
  BrowseAllPatientKind,
  BrowseProductLine,
  IBrowseFilterSpeciesKind,
  IClientBrowseCategory,
} from "../types";

import styles from "../styles/BrowseCatalog.module.less";

const VIEW_NAME = "Browse Catalog";

type FeaturedSectionProps = {
  productLines: BrowseProductLine[];
};

const FeaturedSection = ({ productLines }: FeaturedSectionProps) => {
  const itemListRef = useViewItemListOnVisible(productLines, `${VIEW_NAME} - Featured`);

  return (
    <div className={styles.category}>
      <div className={styles.info}>
        <div className={styles.titles}>
          <h3 className={styles.title}>
            Browse <span className={styles.accent}>popular</span> medications
          </h3>
        </div>
      </div>
      <HorizontalOverflow
        ref={itemListRef}
        direction="right"
        classNames={{ scroller: styles.products, button: styles.button }}
      >
        <CategoryCard.TopSellers className={styles.card} />
        {productLines.map((productLine) => (
          <div key={productLine.id} className={styles.card}>
            <ProductLineCard productLine={productLine} eventViewName={VIEW_NAME} />
          </div>
        ))}
      </HorizontalOverflow>
    </div>
  );
};

type CategorySectionProps = {
  category: IClientBrowseCategory;
};

const CategorySection = ({ category }: CategorySectionProps) => {
  const itemListRef = useViewItemListOnVisible(
    category.product_lines,
    `${VIEW_NAME} - ${category.name}`,
  );

  return (
    <div className={styles.category}>
      <div className={styles.info}>
        <div className={styles.titles}>
          <h3 className={styles.title}>{category.extended_name}</h3>
        </div>
        <PillButton
          size="s"
          variant="secondary"
          dest={urls.products.category({ params: { id: category.id } })}
        >
          View All
        </PillButton>
      </div>
      <HorizontalOverflow
        ref={itemListRef}
        direction="right"
        classNames={{ scroller: styles.products, button: styles.button }}
      >
        <CategoryCard category={category} className={styles.card} />
        {category.product_lines.map((productLine) => (
          <div key={productLine.id} className={styles.card}>
            <ProductLineCard productLine={productLine} eventViewName={VIEW_NAME} />
          </div>
        ))}
      </HorizontalOverflow>
    </div>
  );
};

const BrowseCatalogV1 = () => {
  const [species, setSpecies] = useState<IBrowseFilterSpeciesKind>(BrowseAllPatientKind.ALL);
  const clinic = useClinic();
  const { data: browse, isLoading, error } = useBrowseQuery({ species }, { enabled: true });

  if (isLoading) {
    return <LoadingContainer />;
  }

  if (error || !browse) {
    return <FetchErrorContainer error={error} entityName="categories" isPlural={true} />;
  }

  return (
    <div>
      <Body>
        <div className={styles.top}>
          <div className={styles.heading}>
            <div className={styles.titles}>
              <div className={styles.label}>Welcome to our new home delivery portal</div>
              <h1 className={styles.title}>Browse online store</h1>
            </div>
            <CatalogFilters species={species} setSpecies={setSpecies} clinic={clinic} />
          </div>
          <div className={styles.subheading}>
            <HorizontalOverflow
              direction="right"
              classNames={{ scroller: styles.categories, next: styles.next, back: styles.back }}
            >
              {browse.categories.map((category) => (
                <CategoryCard.Small
                  key={category.id}
                  category={category}
                  className={styles.smallCard}
                />
              ))}
            </HorizontalOverflow>
          </div>
        </div>
      </Body>
      <div className={styles.main}>
        <Container>
          <div className={styles.categories}>
            <FeaturedSection productLines={browse.featured_product_lines} />
            {browse.categories.map((category) => (
              <CategorySection key={category.id} category={category} />
            ))}
          </div>
        </Container>
      </div>
      <div className={styles.support}>
        <Container>
          <BrowseSupport />
        </Container>
      </div>
      <PreFooter image={clinic.storefront_prefooter_image} />
    </div>
  );
};

export default BrowseCatalogV1;
